// Norm-group label + sort helpers, shared by every 3M report that buckets rows
// by the BOQ code hierarchy (3M Deviation Summary, Manpower Cost Analysis).
//
// These live here rather than in one report's own shared file because the label
// FORMAT and the SORT ORDER must be identical across reports: two pages that
// bucket the same rows differently read as two different answers to the same
// question. The backend resolves the group (src/utils/norm-group.util.ts) and
// sends level1Code/level1Name/groupCode/groupName per row; this file is only
// about how those four fields become a heading.

/** Present = the backend resolved it. '' / undefined = it could not. */
const has = (value?: string): boolean =>
  value !== undefined && value !== null && value !== "";

/**
 * `B3 — Underground Excavation`, or null when the row carries no group.
 * Returning null rather than a placeholder is deliberate: callers supply a
 * fallback that means something in their own domain (a source, a head, a
 * category) instead of sweeping un-normed rows into a meaningless bucket.
 */
export const groupOf = (code?: string, name?: string): string | null =>
  has(code) ? `${code}${has(name) ? ` — ${name}` : ""}` : null;

/**
 * Sort key for a group label so `B9` precedes `B10`. A plain string sort puts
 * B10 first, which reads as broken next to a BOQ. Non-coded labels (the
 * callers' fallbacks) sort last, alphabetically.
 */
export const compareGroupLabels = (a: string, b: string): number => {
  const coded = (s: string) => /^B\d/i.test(s);
  if (coded(a) !== coded(b)) return coded(a) ? -1 : 1;
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
};
